import { useAction, useAtom } from '@reatom/npm-react'
import BigNumber from 'bignumber.js'
import { motion } from 'motion/react'
import { useState } from 'react'
import styled from 'styled-components'

import { CurrencyNetworkIcon } from '@root/entities/currency/ui'
import { selectedNetworkAtom } from '@root/entities/network'
import { tradingWalletAtom } from '@root/features/trade/trading'
import { setTradingWalletAction } from '@root/features/trade/trading/atoms'
import { useBreakpoint } from '@root/shared'
import { AmountFormatted } from '@root/shared/core/format'
import { useOptionalAtom } from '@root/shared/lib'
import { ArrowDown2Icon, WalletLine2Icon } from '@root/shared/ui-kit'
import { WalletSelectorDrawer } from '@root/widgets/wallet'

import { WalletsDropdownPopover } from './desktop/wallets-dropdown-popover'

const zero = BigNumber(0)

export const WalletsDropdown = () => {
  const [network] = useAtom(selectedNetworkAtom)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const isMobile = useBreakpoint() === 'mobile'
  const [selectedWallet] = useAtom(tradingWalletAtom)
  const setWallet = useAction(setTradingWalletAction)

  const walletNativeBalance =
    useOptionalAtom(selectedWallet?.nativeAssetBalance) ?? zero

  const renderContent = () => {
    return (
      <TriggerButton
        layout
        transition={{ duration: 0.15, ease: 'easeInOut' }}
        onClick={() => {
          if (isMobile) {
            setDrawerOpen(true)
          }
        }}
      >
        <Label>
          <WalletsCounter
            layout
            transition={{ duration: 0.15, ease: 'easeInOut' }}
          >
            <WalletIcon size={18} />
          </WalletsCounter>
          <Separator />
          <AmountFormatted amount={walletNativeBalance} roundingMode="trunc" />
        </Label>
        <CurrencyNetworkIcon network={network} size={16} />
        <Arrow size={12} />
      </TriggerButton>
    )
  }

  if (!isMobile) {
    return <WalletsDropdownPopover>{renderContent()}</WalletsDropdownPopover>
  }

  return (
    <>
      {renderContent()}
      <WalletSelectorDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        selected={selectedWallet?.id}
        onWalletClick={(wallet) => {
          setWallet(wallet.id)
          setDrawerOpen(false)
        }}
      />
    </>
  )
}

const Arrow = styled(ArrowDown2Icon)``

const WalletsCounter = styled(motion.div)`
  display: grid;
  grid-auto-flow: column;
  gap: 3px;
  align-items: center;
  align-content: center;
`

const TriggerButton = styled(motion.button)`
  all: unset;

  border-radius: 6px;
  background: var(--appearanceA-2);

  color: var(--text-12);

  display: grid;
  overflow: hidden;
  grid-auto-flow: column;

  border: 0;
  gap: 5px;
  align-items: center;
  cursor: pointer;
  position: relative;

  height: 26px;
  padding-inline: 6px;
  font-weight: 600;
  font-size: 11px;

  transition:
    background 0.15s ease-in-out,
    color 0.15s ease-in-out;

  &[aria-expanded='true'] {
    background: var(--appearanceA-7);
  }

  &:hover {
    background: var(--appearanceA-7);
  }

  &[aria-expanded='true'] {
    ${Arrow} {
      transform: rotate(180deg);
    }
  }
`

const Label = styled.span`
  color: var(--text-12);

  font-size: 11px;
  font-weight: 600;
  line-height: 16px;

  display: grid;
  grid-auto-flow: column;
  gap: 5px;

  transition: all 0.15s ease-in-out;
  align-content: center;
  align-items: center;
`

const Separator = styled.div`
  background: #282828;
  width: 1px;
  height: 14px;
  border-radius: 2px;
`

const WalletIcon = styled(WalletLine2Icon)`
  color: var(--text-9);
`
